// Reads holdings out of a brokerage screenshot in the browser. The image never
// leaves the visitor's machine: Tesseract runs in a web worker, and the text it
// produces goes through the heuristics below. Numbers can be misread, so every
// caller shows the result for checking before it is used.

const HEADER_WORDS = new Set([
  'TOTAL', 'QTY', 'QUANTITY', 'AVG', 'COST', 'PRICE', 'VALUE', 'MARKET', 'GAIN', 'LOSS', 'DAY', 'CHANGE',
  'SYMBOL', 'TICKER', 'NAME', 'SHARES', 'HOLDINGS', 'HOLDING', 'PORTFOLIO', 'CASH', 'INVESTED', 'CURRENT',
  'RETURN', 'RETURNS', 'PL', 'LTP', 'NSE', 'BSE', 'USD', 'INR', 'BUY', 'SELL', 'NET', 'ALL', 'ETF', 'STOCK',
  'STOCKS', 'EQUITY', 'MTM', 'AMOUNT', 'UNITS', 'ASSET', 'ASSETS', 'POSITION', 'POSITIONS', 'PRODUCT',
  'DIV', 'YIELD', 'LAST', 'OPEN', 'HIGH', 'LOW', 'CLOSE', 'VOL', 'BID', 'ASK', 'PCT', 'TODAY', 'WEEK',
  'MONTH', 'YEAR', 'YTD', 'CAGR', 'XIRR', 'DELIVERY', 'INTRADAY', 'MARGIN', 'FUNDS', 'AVAILABLE',
]);

const TICKER_RE = /\b[A-Z][A-Z0-9&-]{1,9}(?:\.[A-Z]{1,2})?\b/g;
const NUM_RE = /[-+]?\d[\d,]*(?:\.\d+)?/g;

export function parseOcrText(text) {
  const holdings = [];
  const lines = String(text || '').split(/\r?\n/).map((l) => l.trim()).filter(Boolean);

  lines.forEach((line) => {
    const upper = line.toUpperCase();
    const candidates = [...upper.matchAll(TICKER_RE)]
      .map((m) => ({ t: m[0], i: m.index }))
      .filter(({ t }) => !HEADER_WORDS.has(t) && !/^\d/.test(t) && /[A-Z]{2}/.test(t));
    if (!candidates.length) return;
    const { t: ticker, i } = candidates[0];
    const after = upper.slice(i + ticker.length);
    const nums = [...after.matchAll(NUM_RE)]
      .map((m) => parseFloat(m[0].replace(/,/g, '')))
      .filter((n) => Number.isFinite(n) && n > 0);
    if (!nums.length) return;
    // Share count: the first whole number. Average cost: the number right after it,
    // which is where broker tables put it (a round cost like 3,650.00 is still the cost).
    let qty = nums.find((n) => Number.isInteger(n) && n < 1e7);
    if (qty == null) qty = nums[0];
    const rest = nums.slice(nums.indexOf(qty) + 1);
    const price = rest[0];
    const existing = holdings.find((h) => h.ticker === ticker);
    if (existing) existing.quantity += qty;
    else holdings.push({ ticker, quantity: qty, avgPrice: price != null ? Number(price.toFixed(2)) : '' });
  });

  return { holdings, linesRead: lines.length };
}

async function toCanvas(file, maxDim = 2000) {
  const bitmap = await createImageBitmap(file);
  let { width, height } = bitmap;
  // Small screenshots OCR better when upscaled; huge ones are slow.
  let scale = 1;
  if (Math.max(width, height) < 1000) scale = 2;
  if (Math.max(width, height) * scale > maxDim) scale = maxDim / Math.max(width, height);
  width = Math.round(width * scale);
  height = Math.round(height * scale);
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(bitmap, 0, 0, width, height);
  bitmap.close?.();
  return canvas;
}

// Resolves { holdings, linesRead }. onProgress receives 0..1 while recognising.
export async function extractHoldingsFromImage(file, onProgress) {
  if (!file || !file.type || !file.type.startsWith('image/')) {
    throw new Error('Please choose an image (a screenshot of your holdings).');
  }
  const { createWorker } = await import('tesseract.js');
  const worker = await createWorker('eng', 1, {
    logger: (m) => {
      if (m.status === 'recognizing text' && onProgress) onProgress(m.progress || 0);
    },
  });
  try {
    const canvas = await toCanvas(file);
    const { data } = await worker.recognize(canvas);
    return parseOcrText(data.text);
  } finally {
    await worker.terminate();
  }
}

export function holdingsToText(holdings) {
  return holdings.map((h) => `${h.ticker} ${h.quantity}`).join(', ');
}
